import { useWatch } from 'react-hook-form';

import { handleSaveCardOnFileMutation } from '@getbento/payments/helpers/handleSaveCardOnFileMutation';
import type { PartialSaveOptions } from 'payments/types/form';
import { useLoadingSpinner } from 'reservations/base/providers/LoadingSpinnerProvider';
import { ModalType, useModal } from 'reservations/base/providers/ModalProvider';
import { useBookingDetails } from 'reservations/booking/providers/BookingProvider';
import { BaseChecklessPaymentsButton } from 'reservations/checklessPayments/components/BaseChecklessPaymentsButton';
import { useChecklessPaymentMethod } from 'reservations/checklessPayments/hooks/useChecklessPaymentMethod';
import { usePaymentProviderSubmission } from 'reservations/payments/hooks/usePaymentProviderSubmission';
import { useSaveCardOnFilePayload } from 'reservations/payments/hooks/useSaveCardOnFilePayload';
import { useSubmitSavedPaymentMethod } from 'reservations/payments/hooks/useSubmitSavedPaymentMethod';
import { useSaveCardOnFileMutation } from 'reservations/payments/queries/useSaveCardOnFileMutation';

interface SaveButtonProps {
  disabled?: boolean;
  buttonText?: string;
  dataTestId?: string;
  handleClick: () => void;
}

// Used to save a pre-existing payment method that has been selected
export const SaveExistingPaymentButton = (props: SaveButtonProps) => {
  const { id: savedPaymentMethodId } = useChecklessPaymentMethod();
  return (
    <BaseSavePaymentButton
      savedPaymentMethod={savedPaymentMethodId}
      useSubmission={useSubmitSavedPaymentMethod}
      dataTestId="save-existing-payment-button"
      {...props}
    />
  );
};

// Used to generate and immediately save a token to create a new payment method
export const SaveNewPaymentButton = (props: SaveButtonProps) => (
  <BaseSavePaymentButton
    useSubmission={usePaymentProviderSubmission}
    dataTestId="save-new-payment-button"
    {...props}
  />
);

// Used to save a token that had been generated during a previous step to create a new payment method
export const SaveTokenButton = (props: SaveButtonProps) => {
  const [token, cardDetails] = useWatch({ name: ['token', 'cardDetails'] });
  const useSaveToken = (onSuccess: () => void, on409Error?: () => void) => {
    const saveCardOnFileMutation = useSaveCardOnFileMutation(
      onSuccess,
      on409Error
    );
    return (payload: PartialSaveOptions) =>
      handleSaveCardOnFileMutation(
        payload,
        saveCardOnFileMutation.mutate,
        token,
        cardDetails
      );
  };

  return (
    <BaseSavePaymentButton
      useSubmission={useSaveToken}
      dataTestId="save-token-button"
      {...props}
    />
  );
};

interface BaseSaveButtonProps extends SaveButtonProps {
  useSubmission:
    | typeof useSubmitSavedPaymentMethod
    | typeof usePaymentProviderSubmission;
  savedPaymentMethod?: string;
}

export const BaseSavePaymentButton = ({
  useSubmission,
  savedPaymentMethod,
  disabled = false,
  buttonText = 'SAVE',
  dataTestId,
  handleClick
}: BaseSaveButtonProps) => {
  const { open, currentModal } = useModal();
  const { handleHasLoaded, handleIsLoading } = useLoadingSpinner();

  const on409Error = () => {
    open(ModalType.ChecklessOtp, { nextModal: currentModal });
  };

  const onSuccess = () => {
    handleClick();
    handleHasLoaded();
  };

  const handleSubmission = useSubmission(onSuccess, on409Error);
  const booking = useBookingDetails();
  const payload = useSaveCardOnFilePayload(booking, false, savedPaymentMethod);
  const onSubmit = () => handleSubmission(payload);

  const handleSubmit = () => {
    handleIsLoading('');
    onSubmit();
  };

  return (
    <BaseChecklessPaymentsButton
      buttonText={buttonText}
      handleClick={handleSubmit}
      disabled={disabled}
      dataTestId={dataTestId}
    />
  );
};
